/**
 * 생산지시서 · 재단표
 * - 도면 결과(벽·하리·칸막이 패널 + 천장/바닥 패널)를 구분·사이즈·결합 타입별로 묶어 수량 집계
 * - 철판 사이즈: 이전 시스템 실제 기록(ESTI_PANEL 46,052건) 규칙
 *     벽: 표준(900·441) +16, 재단 +114  /  천장·바닥: 900 +16, 그 외 −79 (첫 패널 995 → 916)
 */
import { PolyPanel, EndType } from '../engine/polyEngine';
import { CeilingPanel, Door, DrawingInfo, Drain, MiddleWall } from '../types';
import { numberPanels, ceilingNo, floorNo, compressNos } from './panelNumbers';

export type SheetKind = '벽' | '칸막이' | '천장' | '바닥';
export interface SheetRow {
  kind: SheetKind;
  size: number;       // 패널 폭 (mm)
  length: number;     // 패널 길이 (mm) — 벽·칸막이 = 벽고, 천장·바닥 = 패널 길이
  joint: string;      // 결합 (양 끝 타입, 순서 무관) 예: "양수·음수"
  qty: number;
  standard: boolean;  // 표준(기성) 패널 / 재단 패널
  steel: number;      // 철판 사이즈 (mm)
  area: number;       // 면적 합 (㎡)
  nos: string[];      // 패널 번호들 (W01, C03 …)
}

const NAME: Record<EndType, string> = { corner: '코너', yangsu: '양수', eumsu: '음수', none: '없음' };
const ORDER: EndType[] = ['corner', 'yangsu', 'eumsu', 'none'];
export const jointLabel = (a: EndType, b: EndType) => [a, b].sort((x, y) => ORDER.indexOf(x) - ORDER.indexOf(y)).map(t => NAME[t]).join('·');

export function wallSteel(size: number, std: number, half: number) { return size === std || size === half ? size + 16 : size + 114; }
export function ceilingSteel(size: number, std: number) { return size === std ? size + 16 : size - 79; }

export interface SheetInput {
  panels: PolyPanel[];                // 벽·하리·칸막이 (칸막이는 pieceKey가 'part:'로 시작)
  ceiling: CeilingPanel[];
  floor: boolean;                     // 바닥 있음 → 천장과 같은 구성으로 한 벌 더
  wallHeight: number;
  std: number; half: number;
}

export function buildSheet(inp: SheetInput): SheetRow[] {
  const map = new Map<string, SheetRow>();
  const add = (kind: SheetKind, size: number, length: number, joint: string, standard: boolean, steel: number, no: string) => {
    const k = `${kind}|${size}|${length}|${joint}`;
    const r = map.get(k) ?? { kind, size, length, joint, qty: 0, standard, steel, area: 0, nos: [] };
    r.qty++; r.area += (size * length) / 1e6; r.nos.push(no);
    map.set(k, r);
  };
  const nos = numberPanels(inp.panels);
  inp.panels.forEach(p => {
    const kind: SheetKind = p.pieceKey.startsWith('part:') ? '칸막이' : '벽';
    const standard = p.size === inp.std || p.size === inp.half;
    add(kind, p.size, inp.wallHeight, jointLabel(p.startType, p.endType), standard, wallSteel(p.size, inp.std, inp.half), nos[p.id]);
  });
  const ceil = (kind: SheetKind) => inp.ceiling.forEach((c, i) => {
    const edge = i === 0 && c.size !== inp.std && !c.isCustom; // 벽 위 첫 패널 (두께별 규격)
    add(kind, c.size, c.length, jointLabel(c.leftType as EndType, c.rightType as EndType), c.size === inp.std || edge, ceilingSteel(c.size, inp.std), kind === '천장' ? ceilingNo(i) : floorNo(i));
  });
  ceil('천장');
  if (inp.floor) ceil('바닥');
  const kindOrder: SheetKind[] = ['벽', '칸막이', '천장', '바닥'];
  return Array.from(map.values()).sort((a, b) =>
    kindOrder.indexOf(a.kind) - kindOrder.indexOf(b.kind) || Number(b.standard) - Number(a.standard) || b.size - a.size || a.joint.localeCompare(b.joint));
}

const esc = (s: string) => s.replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

export interface PrintSheetOptions {
  rows: SheetRow[];
  info: DrawingInfo;
  spec: { horizontal: number; vertical: number; height: number; wallHeight: number; thick: number; material: string; ceiling: boolean; floor: boolean };
  doors: Door[];
  drains: Drain[];
  middleWalls: MiddleWall[];
  scene?: ((w: number, h: number) => HTMLCanvasElement) | null;
}

/** 생산지시서(1쪽: 도면 + 정보 + 부자재) + 재단표(2쪽) 인쇄 창 */
export function printProductionSheet(o: PrintSheetOptions) {
  const img = o.scene ? o.scene(1500, 1000).toDataURL('image/png') : '';
  const s = o.spec;
  const doorList = o.doors.map(d => `${d.width}×${d.height} (${d.doorType === 'double' ? '양문' : '단문'})`);
  const doorAgg = Object.entries(doorList.reduce<Record<string, number>>((m, k) => ({ ...m, [k]: (m[k] ?? 0) + 1 }), {}))
    .map(([k, n]) => `${k} × ${n}`).join(', ') || '없음';
  const info: [string, string][] = [
    ['현장명', o.info.siteName], ['도면번호', o.info.drawingNo], ['작성일', o.info.date], ['출고일', o.info.shipDate],
    ['작성자', o.info.drawnBy], ['연락처', o.info.contact],
    ['재질', `${s.material} ${s.thick}T`], ['규격', `${s.horizontal} × ${s.vertical} × ${s.height}`], ['벽고', `${s.wallHeight}`],
    ['천장 / 바닥', `${s.ceiling ? '있음' : '없음'} / ${s.floor ? '있음' : '없음'}`],
    ['문', doorAgg], ['배수구', `${o.drains.length}개`], ['칸막이', `${o.middleWalls.length}개`], ['비고', o.info.memo],
  ];
  const kinds: SheetKind[] = ['벽', '칸막이', '천장', '바닥'];
  const body = kinds.filter(k => o.rows.some(r => r.kind === k)).map(k => {
    const rs = o.rows.filter(r => r.kind === k);
    const qty = rs.reduce((a, r) => a + r.qty, 0), area = rs.reduce((a, r) => a + r.area, 0);
    return rs.map((r, i) => `<tr class="${r.standard ? '' : 'cut'}">
        ${i === 0 ? `<td rowspan="${rs.length + 1}" class="kind">${k}</td>` : ''}
        <td class="num">${r.size}</td><td class="num">${r.length}</td><td>${r.joint}</td>
        <td class="num b">${r.qty}</td><td>${r.standard ? '표준' : '재단'}</td><td class="num">${r.steel}</td><td class="num">${r.area.toFixed(2)}</td>
        <td class="nos">${compressNos(r.nos)}</td></tr>`).join('')
      + `<tr class="sub"><td colspan="3">${k} 소계</td><td class="num b">${qty}</td><td></td><td></td><td class="num">${area.toFixed(2)}</td><td></td></tr>`;
  }).join('');
  const totalQty = o.rows.reduce((a, r) => a + r.qty, 0), totalArea = o.rows.reduce((a, r) => a + r.area, 0);
  const cutQty = o.rows.filter(r => !r.standard).reduce((a, r) => a + r.qty, 0);
  const title = esc(o.info.siteName || 'Panel Draw');
  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>생산지시서 - ${title}</title>
<style>
  @page { size: A4 landscape; margin: 8mm; }
  * { box-sizing: border-box; }
  body { font-family: "Malgun Gothic", Arial, sans-serif; margin: 0; color: #222; }
  .page { page-break-after: always; display: flex; gap: 10px; height: 190mm; }
  .page:last-child { page-break-after: auto; }
  h1 { font-size: 20px; margin: 0 0 6px; letter-spacing: 4px; }
  .draw { flex: 1; border: 1.5px solid #000; display: flex; align-items: center; justify-content: center; }
  .draw img { max-width: 100%; max-height: 100%; }
  .side { width: 260px; display: flex; flex-direction: column; }
  table { border-collapse: collapse; width: 100%; font-size: 11px; }
  th, td { border: 1px solid #999; padding: 3px 6px; }
  th { background: #E3F2FD; }
  .info th { width: 72px; text-align: left; background: #f5f5f5; }
  .num { text-align: right; font-variant-numeric: tabular-nums; }
  .b { font-weight: bold; }
  .kind { text-align: center; font-weight: bold; background: #fafafa; }
  tr.cut td { background: #FFF3E0; }
  tr.sub td { background: #eee; font-weight: bold; }
  .cutpage { display: block; height: auto; }
  .sum { margin: 6px 0; font-size: 12px; }
  td.nos { font-size: 10px; color: #444; max-width: 260px; }
</style></head><body>
<div class="page">
  <div class="draw">${img ? `<img src="${img}">` : ''}</div>
  <div class="side">
    <h1>생산지시서</h1>
    <table class="info">${info.map(([k, v]) => `<tr><th>${k}</th><td>${esc(v || '')}</td></tr>`).join('')}</table>
  </div>
</div>
<div class="page cutpage">
  <h1>재단표 <span style="font-size:13px;letter-spacing:0">— ${title} / ${esc(s.material)} ${s.thick}T / ${s.horizontal}×${s.vertical}</span></h1>
  <div class="sum">총 <b>${totalQty}</b>장 (재단 ${cutQty}장 · 표준 ${totalQty - cutQty}장) / 면적 <b>${totalArea.toFixed(2)}</b>㎡ — 주황색 줄 = 재단 패널</div>
  <table>
    <tr><th>구분</th><th>폭(mm)</th><th>길이(mm)</th><th>결합</th><th>수량</th><th>종류</th><th>철판(mm)</th><th>면적(㎡)</th><th>패널 번호</th></tr>
    ${body}
    <tr class="sub"><td colspan="4">합계</td><td class="num b">${totalQty}</td><td></td><td></td><td class="num">${totalArea.toFixed(2)}</td><td></td></tr>
  </table>
</div>
<script>window.onload = () => setTimeout(() => window.print(), 300);</script>
</body></html>`;
  const w = window.open('', '_blank');
  if (!w) { alert('팝업이 차단되었습니다. 브라우저에서 팝업을 허용해 주세요.'); return; }
  w.document.write(html);
  w.document.close();
}
